import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  Feature,
  Context,
  DeletedTreatments,
  EnrichedTreatment,
} from "./stateInterfaces";
import { Segment } from "Interfaces/FeatureFlip";
import { Treatment } from "API/Queries/useGetAllTreatments";

interface TreatmentType extends Treatment {
  color?: string;
}

interface FeatureFlipState {
  features: Feature[];
  contexts: Context[];
  segments: Segment[];
  selectedProjectId: string;
  searchTerm: string;
  hasMoreDataFeatures: boolean;
  hasMoreDataSegments: boolean;
  pageFeatures: number;
  pageContexts: number;
  pageSegments: number;

  // Features actions
  setFeatures: (features: Feature[]) => void;

  // Contexts actions
  setContexts: (contexts: Context[]) => void;

  // Segments actions
  setSegments: (segments: Segment[]) => void;
  // Project actions
  setSelectedProject: (projectId: string) => void;

  // Search & pagination actions
  setSearchTerm: (searchTerm: string) => void;
  setHasMoreDataFeatures: (hasMoreData: boolean) => void;
  setHasMoreDataSegments: (hasMoreData: boolean) => void;
  setPageFeatures: (page: number) => void;
  setPageSegments: (page: number) => void;
}

interface FeatureFlipFeaturePageState {
  existingTreatments: Treatment[];
  newTreatments: TreatmentType[];
  deletedTreatments: DeletedTreatments[];
  editedTreatments: Treatment[];
  isFeatureEnabled: boolean;
  isFeatureDynamic: boolean;
  isSaving: boolean;
  duplicateErrors: Record<string, string>;
  colors: string[];
}

const featurePageInitialState: FeatureFlipFeaturePageState = {
  existingTreatments: [],
  newTreatments: [],
  deletedTreatments: [],
  editedTreatments: [],
  isFeatureEnabled: false,
  isFeatureDynamic: false,
  isSaving: false,
  duplicateErrors: {},
  colors: [],
};

interface FeatureFlipFeaturePageActions {
  setExistingTreatments: (treatments: Treatment[]) => void;
  setNewTreatments: (treatments: TreatmentType[]) => void;
  setDeletedTreatments: (treatments: DeletedTreatments[]) => void;
  setEditedTreatments: (treatments: Treatment[]) => void;
  setIsFeatureEnabled: (isEnabled: boolean) => void;
  setIsFeatureDynamic: (isDynamic: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
  setDuplicateErrors: (errors: Record<string, string>) => void;
  setColors: (colors: string[]) => void;
}

interface FeatureFlipInterface
  extends FeatureFlipState,
    FeatureFlipFeaturePageState,
    FeatureFlipFeaturePageActions {}

export const useFeatureFlipStore = create<FeatureFlipInterface>()(
  devtools(
    (set) => ({
      features: [],
      contexts: [],
      segments: [],
      searchTerm: "",
      selectedProjectId: "",
      hasMoreDataFeatures: true,
      hasMoreDataSegments: true,
      pageFeatures: 1,
      pageContexts: 1,
      pageSegments: 1,
      ...featurePageInitialState,
      // Features actions
      setFeatures: (features) => set({ features }, false, "setFeatures"),

      // Contexts actions
      setContexts: (contexts) => set({ contexts }, false, "setContexts"),

      // Segments actions
      setSegments: (segments) => set({ segments }, false, "setSegments"),

      // Project actions
      setSelectedProject: (projectId) =>
        set({ selectedProjectId: projectId }, false, "setSelectedProject"),

      // Search & pagination actions
      setSearchTerm: (searchTerm) =>
        set({ searchTerm }, false, "setSearchTerm"),
      setHasMoreDataFeatures: (hasMoreData) =>
        set(
          { hasMoreDataFeatures: hasMoreData },
          false,
          "setHasMoreDataFeatures"
        ),
      setHasMoreDataSegments: (hasMoreData) =>
        set(
          { hasMoreDataSegments: hasMoreData },
          false,
          "setHasMoreDataSegments"
        ),
      setPageFeatures: (page) =>
        set({ pageFeatures: page }, false, "setPageFeatures"),
      setPageSegments: (page) =>
        set({ pageSegments: page }, false, "setPageSegments"),

      // Feature page actions
      setExistingTreatments: (treatments) =>
        set({ existingTreatments: treatments }, false, "setExistingTreatments"),
      setNewTreatments: (treatments) =>
        set({ newTreatments: treatments }, false, "setNewTreatments"),
      setDeletedTreatments: (treatments) =>
        set({ deletedTreatments: treatments }, false, "setDeletedTreatments"),
      setEditedTreatments: (treatments) =>
        set({ editedTreatments: treatments }, false, "setEditedTreatments"),
      setIsFeatureEnabled: (isEnabled) =>
        set({ isFeatureEnabled: isEnabled }, false, "setIsFeatureEnabled"),
      setIsFeatureDynamic: (isDynamic) =>
        set({ isFeatureDynamic: isDynamic }, false, "setIsFeatureDynamic"),
      setIsSaving: (isSaving) =>
        set({ isSaving: isSaving }, false, "setIsSaving"),
      setDuplicateErrors: (errors) =>
        set({ duplicateErrors: errors }, false, "setDuplicateErrors"),
      setColors: (colors) => set({ colors }, false, "setColors"),
    }),
    {
      name: "FeatureFlip Store",
      enabled: true,
    }
  )
);
